// =============================================================================
// now-banner.js – Sticky header for quick outbound departure countdowns
// =============================================================================

import { escapeHtml, minsToHHMM } from "./utils.js";
import { getLeaveAtMs } from "./live-cards.js";
import { getThresholdsFromState, classifyConnection } from "./connection-classifier";
import { calcNextTrain } from "./trains";
import { getStopName } from "./line-config.js";

/**
 * Checks for train/metro connections at the destination stops of the hero card.
 */
export function getHeroConnectionInfo(card, cfg, state) {
  const trip = card.trip;
  if (!trip || card.direction === "return") return null;

  const thresholds = getThresholdsFromState(state, cfg);
  const connectionStops = [...new Set([...(card.detailStops || []), ...(card.compactStops || [])])];

  for (const code of connectionStops) {
    const arrMin = trip.stops?.[code];
    const interchange = cfg.interchanges?.[code];
    if (arrMin === undefined || arrMin === null || !interchange) continue;

    if (interchange.trainSlot) {
      const train = calcNextTrain(interchange.trainSlot, arrMin)[0];
      if (train) {
        const connState = classifyConnection(train.waitMin, thresholds);
        return {
          type: "train",
          line: train.line,
          state: connState,
          waitMin: train.waitMin,
          station: interchange.label || ""
        };
      }
    } else if (interchange.type === "M1") {
      return {
        type: "metro",
        line: "M1",
        state: { label: "frequente", css: "calm" },
        waitMin: 0,
        station: interchange.label || ""
      };
    }
  }
  return null;
}

/**
 * Renders the HTML for the sticky now banner.
 * Only visible in outbound direction when a bus is departure-bound.
 */
export function renderNowBanner(vm, currentMin, card, cfg, state) {
  if (vm.direction !== "outbound") {
    return "";
  }

  const tripDepMin = vm.trip?._depMin ?? 0;
  const leaveAtMs = getLeaveAtMs(tripDepMin, vm.walkMin, currentMin);

  const conn = getHeroConnectionInfo(card, cfg, state);
  let connStr = "";
  if (conn) {
    connStr = ` &middot; coinc. ${escapeHtml(conn.line)} <span class="conn-state-${conn.state.css}">${escapeHtml(conn.state.label)}</span>`;
  }

  const urgencyClass = vm.urgency.css; // "calm" | "good" | "hurry" | "missed"

  return `
    <div id="now-banner" class="now-banner now-banner--${urgencyClass}">
      <span class="now-banner-text">
        🚶 Esci <span data-leave-ts="${leaveAtMs}">tra ${vm.leaveCountdown} min</span> → 
        <span class="now-banner-line">${escapeHtml(vm.lineId)}</span> 
        <strong>${vm.depHHMM}</strong> da ${escapeHtml(vm.fromStopName)}${connStr}
      </span>
    </div>
  `;
}

/**
 * Updates the now-banner urgency CSS classes in-place based on actual seconds left.
 */
export function updateNowBanner(bannerEl, vm, currentMin) {
  if (!bannerEl) return;
  const leaveTsNode = bannerEl.querySelector("[data-leave-ts]");
  if (!leaveTsNode) return;

  const targetMs = Number(leaveTsNode.dataset.leaveTs);
  const secsLeft = Math.round((targetMs - Date.now()) / 1000);

  // Reset classes
  bannerEl.className = "now-banner";

  if (secsLeft <= 0) {
    bannerEl.classList.add("now-banner--missed");
  } else if (secsLeft <= 120) {
    bannerEl.classList.add("now-banner--hurry");
  } else if (secsLeft <= 300) {
    bannerEl.classList.add("now-banner--good");
  } else {
    bannerEl.classList.add("now-banner--calm");
  }
}
